import { cn } from '@utils/cn';
import { useEffect } from 'react';

/**
 * Set on `body` while the bar is on screen so the HubSpot chat bubble can move
 * out of its way — the same mechanism the cookie banner uses. See globals.css.
 */
const CHAT_OFFSET_BODY_CLASS = 'sticky-cta-visible';

/**
 * How close the deadline is to bearing on the viewer. `active` means the window
 * is shutting on them — a draw closing, a timed release ending — and is called
 * out in orange. `upcoming` means it has not opened yet, and reads as plain copy.
 */
export type StickyCtaUrgencyTone = 'active' | 'upcoming';

interface Props {
  children: React.ReactNode;
  /** Deadline — a countdown, or a date once it is more than six days off. */
  urgency?: React.ReactNode;
  urgencyTone?: StickyCtaUrgencyTone;
  visible: boolean;
}

/**
 * Product page call to action pinned to the bottom of the viewport on mobile.
 *
 * Mobile only, and enforced in CSS rather than with a JS breakpoint so the bar
 * is correct on the very first frame — `useMediaQuery` reports false until its
 * effect runs, which cost the bar a render (PTE-4262).
 *
 * Hidden with `visibility` rather than unmounted so the button leaves the focus
 * order and the accessibility tree while it is off screen, but still animates.
 */
export const StickyBottomCta: React.FC<Props> = ({
  children,
  urgency,
  urgencyTone = 'upcoming',
  visible,
}) => {
  useEffect(() => {
    document.body.classList.toggle(CHAT_OFFSET_BODY_CLASS, visible);

    return () => document.body.classList.remove(CHAT_OFFSET_BODY_CLASS);
  }, [visible]);

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-80 border-t border-divider/20 bg-white',
        'md:hidden',
        'pb-[env(safe-area-inset-bottom)]',
        'transition-[transform,visibility] duration-300 ease-in-out',
        'motion-reduce:transition-none',
        visible ? 'visible translate-y-0' : 'invisible translate-y-full',
      )}
      data-testid="sticky-bottom-cta"
    >
      <div className="flex w-full items-center gap-4 px-5 py-3">
        {urgency ? (
          <p
            className={cn(
              'min-w-0 flex-1 truncate',
              urgencyTone === 'active' ? 'text-active-4' : 'text-neutral-6',
            )}
            data-testid="sticky-bottom-cta-urgency"
          >
            {urgency}
          </p>
        ) : null}
        <div className={cn(urgency ? 'shrink-0' : 'w-full')}>{children}</div>
      </div>
    </div>
  );
};
